import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, ShieldCheck, GitBranch,
  CheckCircle2, Building2, HardHat, Wrench, FileText, Flame, Globe2,
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import Workflow from '../components/Workflow';
import IntroVideoModal from '../components/IntroVideoModal';
import ScreenshotGallery from '../components/ScreenshotGallery';
import { introVideo } from '../videos';

/* ------------------------------------------------------------------------- */
/* Static data                                                               */
/* ------------------------------------------------------------------------- */

const PILLARS = [
  { name: 'Projects',   lead: 'Control delivery.',
    body: 'Structure projects from initiation through governance, approvals, delivery, closeout and reporting.' },
  { name: 'Lifecycle',  lead: 'Understand what needs to happen next.',
    body: 'Track lifecycle requirements, priorities, interventions and future needs across assets and portfolios.' },
  { name: 'Assurance',  lead: 'Know whether requirements are being met.',
    body: 'Turn governance, contractual and operational requirements into visible controls, responsibilities and exceptions.' },
  { name: 'Evidence',   lead: 'Prove what happened.',
    body: 'Build evidence capture into delivery rather than searching for records afterwards.' },
];

const NEEDS = [
  { icon: Building2, label: 'NHS',
    q: 'I deliver NHS projects.',
    body: 'Use country-specific Videra Healthcare environments designed around NHS project and lifecycle delivery.',
    cta: 'Explore Videra Healthcare', to: '/videra/healthcare' },
  { icon: Wrench, label: 'Hard Services',
    q: 'I manage Hard Services, lifecycle and projects.',
    body: 'Control lifecycle requirements, projects, approvals, delivery, evidence and progressive handback readiness.',
    cta: 'Explore Videra Hard Services', to: '/videra/hard-services' },
  { icon: HardHat, label: 'Construction',
    q: 'I deliver construction projects.',
    body: 'Bring governance, programme, risk, evidence, project controls and handover into one structured delivery environment.',
    cta: 'Explore Videra Construction', to: '/videra/construction' },
  { icon: GitBranch, label: 'Other industries',
    q: 'I need a platform for my own sector.',
    body: 'Use Videra PM to create a project delivery platform around your industry and organisation.',
    cta: 'Create My Videra', to: '/videra/pm' },
  { icon: ShieldCheck, label: 'PFI / P3',
    q: 'I need to assure contractual and operational obligations.',
    body: 'Use Vanguard to connect contractual requirements, assets, SFG20, evidence, exceptions and assurance.',
    cta: 'Explore Videra Vanguard', to: '/videra/vanguard' },
  { icon: FileText, label: 'Handback',
    q: 'I need to understand handback readiness.',
    body: 'Connect lifecycle, asset condition, operational evidence and contractual assurance to progressively understand the handback position.',
    cta: 'Explore Handback Readiness', to: '/handback-readiness' },
  { icon: Flame, label: 'Firestopping',
    q: 'I manage passive fire protection.',
    body: 'Use SafeTrace independently or manage the wider operation through the Firestopping Business Dashboard.',
    cta: 'Explore Firestopping', to: '/firestopping' },
  { icon: Globe2, label: 'International P3',
    q: 'I operate a P3 or PPP internationally.',
    body: 'Bring UK lifecycle and assurance experience to your local contract and operating model.',
    cta: 'Explore International P3', to: '/international-p3' },
];

/* ------------------------------------------------------------------------- */

const V2Home = () => {
  useSEO({
    title: 'Keystone Consultants | Videra Delivery Platform',
    description: 'Consultancy and platform for NHS capital, healthcare FM, PFI and Tier-1 construction — stage-gated delivery, audit-ready evidence, Golden Thread compliance.',
    path: '/',
  });

  return (
  <div data-testid="v2-home">

    {/* ============= HERO ============= */}
    <section className="relative pt-16 pb-20 md:pt-20 md:pb-24 overflow-hidden" data-testid="v2-home-hero">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
          {/* Left — copy */}
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-6" data-testid="v2-hero-eyebrow">
              Keystone Strategic Consultants · Creators of Videra
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05]">
              Complex delivery needs more than another piece of software.
            </h1>
            <div className="mt-8 text-base lg:text-lg text-slate-700 leading-relaxed space-y-4">
              <p>Keystone combines decades of real-world project, lifecycle and operational experience with Videra — a growing ecosystem of digital platforms designed to help organisations deliver projects, manage lifecycle, assure obligations and maintain the evidence to prove it.</p>
              <p>Choose a solution we&rsquo;ve already built. Create one around your own industry. Or work with us to design a platform around the way your organisation operates.</p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a href="https://demo.videra.app/explore"
                 data-testid="v2-hero-try"
                 className="inline-flex items-center justify-center bg-slate-900 text-white px-7 py-3.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group">
                Try Videra <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <Link to="/contact"
                 data-testid="v2-hero-talk"
                 className="inline-flex items-center justify-center bg-white border border-slate-300 text-slate-900 px-7 py-3.5 rounded-md text-sm font-medium hover:border-slate-900 transition-colors">
                Talk to Keystone <ChevronRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
            <p className="mt-6 text-sm text-slate-500 italic" data-testid="v2-hero-tagline">
              Built from experience. Designed around your world.
            </p>
          </div>

          {/* Right — video poster */}
          <div>
            <IntroVideoModal
              src={introVideo.videoUrl}
              poster="/images/healthcare/hc-portfolio-tracker-hero.jpg"
              kicker="90-sec introduction"
              title="See why we built Videra."
              body="In ninety seconds, Keystone explains why we built Videra and walks through how it works across NHS Capital, Healthcare and FM Hard Services, and Tier-1 Construction."
              testid="v2-home-hero-video"
            />
          </div>
        </div>

        {/* Credibility strip */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            'Live PFI Hard Services use',
            'Project + Lifecycle',
            'NHS-specific platforms',
            '24-day full-function trial',
          ].map((s) => (
            <div key={s} className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold border border-slate-200 rounded-md px-4 py-3 text-center">
              {s}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ============= THE PROBLEM ============= */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-home-problem">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">The problem</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
          Your organisation probably doesn&rsquo;t need more information.
        </h2>
        <p className="mt-3 text-xl md:text-2xl text-slate-500">It needs a better way to connect it.</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-slate-700">
          {[
            'Projects in spreadsheets.',
            'Lifecycle plans in separate files.',
            'Asset information in another system.',
            'Actions buried in email.',
            'Evidence stored across folders and SharePoint.',
            'Contractual requirements sitting inside documents.',
            'Management reports assembled manually every month.',
          ].map((s) => (
            <div key={s} className="flex items-start gap-2 py-1">
              <ChevronRight className="w-4 h-4 text-blue-500 flex-shrink-0 mt-1" />
              <span>{s}</span>
            </div>
          ))}
        </div>
        <p className="mt-8 text-base text-slate-600">
          The information exists. The connections often don&rsquo;t.
        </p>
        <p className="mt-6 text-base text-slate-800">
          Videra brings those connections together so teams can understand:
        </p>
        <ul className="mt-3 space-y-1.5 text-slate-700">
          {['What needs to happen.','Why it needs to happen.','Who owns it.','What has been delivered.','Where the evidence sits.','What needs attention next.'].map((s) => (
            <li key={s} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              {s}
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* ============= FOUR PILLARS ============= */}
    <section className="py-20 md:py-28" data-testid="v2-home-pillars">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Projects · Lifecycle · Assurance · Evidence</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 max-w-3xl">
          One connected view of delivery.
        </h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p) => (
            <div key={p.name} className="border border-slate-200 rounded-xl p-6 bg-white hover:shadow-lg transition-shadow" data-testid={`v2-pillar-${p.name.toLowerCase()}`}>
              <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">{p.name}</p>
              <p className="text-lg font-medium text-slate-900 mb-2">{p.lead}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-lg text-slate-800 tracking-tight">
          Projects. Lifecycle. Assurance. Evidence. <span className="text-blue-600">Connected.</span>
        </p>
      </div>
    </section>

    {/* ============= LIFECYCLE ============= */}
    <section className="py-20 md:py-28 bg-slate-950 text-white" data-testid="v2-home-lifecycle">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-400 font-semibold mb-4">Lifecycle</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight max-w-3xl">
          Projects and lifecycle shouldn&rsquo;t live in different systems.
        </h2>
        <div className="mt-8 max-w-3xl text-slate-300 space-y-3 text-base leading-relaxed">
          <p>A lifecycle requirement often becomes a project.</p>
          <p>That project changes the asset.</p>
          <p>The completed intervention changes the future lifecycle position.</p>
          <p className="text-slate-400">Yet many organisations manage each stage separately. Videra connects the journey.</p>
        </div>
        <div className="mt-10">
          <Workflow variant="dark" testid="v2-home-lifecycle-workflow" steps={[
            'Asset','Condition','Lifecycle requirement','Priority','Project / intervention','Approval','Delivery','Evidence','Updated asset','Future lifecycle position',
          ]} />
        </div>
        <p className="mt-10 text-slate-300 max-w-3xl text-base leading-relaxed">
          Lifecycle management isn&rsquo;t simply predicting when something might need replacing.
          It&rsquo;s understanding condition, risk, operational impact, priority, cost and intervention —
          then controlling the projects that turn the lifecycle plan into reality.
        </p>
      </div>
    </section>

    {/* ============= PRACTITIONER STORY ============= */}
    <section className="py-20 md:py-28" data-testid="v2-home-practitioner">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Practitioner story</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
          Built by practitioners because we&rsquo;ve lived the problems.
        </h2>
        <div className="mt-8 text-base lg:text-lg text-slate-700 leading-relaxed space-y-4">
          <p>Videra didn&rsquo;t start as a software idea. It grew from years of managing complex projects and lifecycle programmes within major contracting and healthcare environments.</p>
          <p>From large acute hospital estates to smaller community facilities, the same challenges repeatedly appeared:</p>
          <ul className="pl-1 space-y-1.5 text-slate-600">
            {['Disconnected information.','Competing lifecycle priorities.','Changing asset condition.','Governance requirements.','Commercial pressure.','Incomplete evidence.','Manual reporting.','And management discovering problems later than it should.'].map((s) => (
              <li key={s} className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-blue-500 flex-shrink-0 mt-1" />
                {s}
              </li>
            ))}
          </ul>
          <p>That experience shaped Videra.</p>
          <p className="text-slate-500 italic">We don&rsquo;t start by asking &ldquo;What features should the software have?&rdquo;<br/>We start by asking &ldquo;What problem does the person doing the job actually need solved?&rdquo;</p>
        </div>
      </div>
    </section>

    {/* ============= LIVE USE ============= */}
    <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="v2-home-live">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Live use</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight max-w-3xl">
          From real-world experience to live operational use.
        </h2>
        <div className="mt-8 text-base lg:text-lg text-slate-700 leading-relaxed max-w-3xl space-y-4">
          <p>Videra has progressed beyond concept and prototype.</p>
          <p>Videra technology is in live use within PFI Hard Services environments involving Tier-1 contractors supporting hospital estates in the North and South of England.</p>
          <p>Our specialist NHS England and NHS Scotland delivery platforms are available to experience, with Wales and Northern Ireland environments in production.</p>
          <p>Videra PM takes the same philosophy into additional industries by allowing organisations to create project delivery environments around their own sector.</p>
        </div>
        <p className="mt-8 text-sm uppercase tracking-[0.22em] text-slate-500 font-semibold">
          Experience → Methodology → Technology → Live deployment → Scale
        </p>
        <div className="mt-8">
          <a href="https://demo.videra.app/explore" className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-testid="v2-home-live-cta">
            Try Videra <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>

    {/* ============= VIDEO (mid-page, alternative entry point) ============= */}
    <section className="py-20 md:py-28" data-testid="v2-home-video">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">90-second introduction</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
              See why we built Videra.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed">
              Discover how practical project and lifecycle experience became the foundation for a different kind of delivery platform.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="https://demo.videra.app/explore" className="inline-flex items-center gap-1 border border-slate-300 text-slate-900 px-6 py-3 rounded-md text-sm font-medium hover:border-slate-900 transition-colors">
                Try Videra live <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div>
            <IntroVideoModal
              src={introVideo.videoUrl}
              poster="/images/nhs/nhs-pmo-command-centre.jpg"
              kicker="Introduction"
              title="Watch the 90-second introduction"
              testid="v2-home-video-mid"
            />
          </div>
        </div>
      </div>
    </section>

    {/* ============= INSIDE VIDERA — real screens ============= */}
    <ScreenshotGallery
      eyebrow="Inside Videra"
      title="Real screens. Real workflows. Real users."
      description="A cross-section of the daily operating environment across NHS Capital, Healthcare & FM Hard Services and Tier-1 Construction."
      shots={[
        { src: '/images/nhs/nhs-portfolio-tracker.jpg', caption: 'NHS Capital multi-Trust portfolio tracker — programme, lifecycle and estates rollup.' },
        { src: '/images/healthcare/hc-portfolio-tracker-hero.jpg', caption: 'Videra Hard Services portfolio dashboard — live PFI hard-services rollup.' },
        { src: '/images/construction/cp-live-portfolio.jpg', caption: 'Tier-1 construction live portfolio — projects at every RIBA stage.' },
        { src: '/images/nhs/nhs-board-pack.jpg', caption: 'AI narrative board pack — plain-English rollup assembled from live data.' },
        { src: '/images/healthcare/hc-handback-readiness.jpg', caption: 'Progressive handback readiness — evidence position by asset and site.' },
        { src: '/images/construction/cp-project-stages.jpg', caption: 'Stage-gated delivery with governance evidence auto-compiled at each gate.' },
        { src: '/images/nhs/nhs-compliance-rails.jpg', caption: 'Compliance rails — HTM/SHTM, IPC and statutory obligations in view.' },
        { src: '/images/healthcare/hc-weekly-governance.jpg', caption: 'Weekly governance meeting — driven from live delivery data.' },
      ]}
      testid="v2-home-screens"
    />

    {/* ============= WHAT DO YOU NEED VIDERA TO DO ============= */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-home-needs">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Start with the problem</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 max-w-3xl">
          What do you need Videra to do?
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {NEEDS.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              className="group bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-900 hover:shadow-lg transition-all"
              data-testid={`v2-need-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center mb-4">
                <n.icon className="w-4 h-4 text-white" />
              </div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold">{n.label}</p>
              <p className="mt-2 text-base font-medium text-slate-900 leading-snug">{n.q}</p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{n.body}</p>
              <p className="mt-4 inline-flex items-center text-xs font-medium text-blue-600 group-hover:text-blue-700">
                {n.cta} <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ============= BUY / BUILD / BUILD WITH US ============= */}
    <section className="py-20 md:py-28" data-testid="v2-home-buybuild">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Three ways to start</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
          Buy it. Build it. Build it with us.
        </h2>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[
            { title: 'Buy it', lead: 'Start with something we&rsquo;ve already built.',
              body: 'Choose a ready-built specialist Videra solution based on real-world sector experience.',
              cta: 'Explore Solutions', to: '/solutions', dark: false },
            { title: 'Build it', lead: 'Create Videra around your world.',
              body: 'Choose your industry and create a structured project delivery environment through Videra PM.',
              cta: 'Create My Videra', href: 'https://pm.videra.app', dark: true },
            { title: 'Build it with us', lead: 'Bring us the problem.',
              body: 'Keystone can map your process, simplify the operating model and configure Videra around your organisation.',
              cta: 'Talk to Keystone', to: '/contact', dark: false },
          ].map((c) => (
            <div key={c.title} className={`rounded-2xl p-8 ${c.dark ? 'bg-slate-950 text-white border border-slate-900' : 'bg-white border border-slate-200'}`} data-testid={`v2-buy-build-${c.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className={`text-[11px] uppercase tracking-[0.22em] font-semibold mb-3 ${c.dark ? 'text-blue-400' : 'text-blue-600'}`}>{c.title}</p>
              <h3 className={`text-xl font-medium mb-3 leading-snug ${c.dark ? 'text-white' : 'text-slate-900'}`} dangerouslySetInnerHTML={{ __html: c.lead }} />
              <p className={`text-sm leading-relaxed ${c.dark ? 'text-slate-300' : 'text-slate-600'}`}>{c.body}</p>
              <div className="mt-8">
                {c.href ? (
                  <a href={c.href} className={`inline-flex items-center gap-1 text-sm font-medium ${c.dark ? 'text-white hover:text-blue-300' : 'text-slate-900 hover:text-blue-700'}`}>
                    {c.cta} <ArrowRight className="w-4 h-4" />
                  </a>
                ) : (
                  <Link to={c.to} className={`inline-flex items-center gap-1 text-sm font-medium ${c.dark ? 'text-white hover:text-blue-300' : 'text-slate-900 hover:text-blue-700'}`}>
                    {c.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ============= LIVE TRIAL ============= */}
    <section className="py-20 md:py-28 bg-slate-950 text-white relative overflow-hidden" data-testid="v2-home-trial">
      <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '32px 32px',
      }} />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-400 font-semibold mb-4">Live trial</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight max-w-3xl">
          Don&rsquo;t just read about Videra. Use it.
        </h2>
        <div className="mt-8 max-w-3xl text-slate-300 text-base lg:text-lg leading-relaxed space-y-4">
          <p>Experience Videra as a fully functional platform.</p>
          <p>Explore ready-built NHS England, NHS Scotland and FM Hard Services environments through a 24-day trial.</p>
          <p>Work with the platform, explore the workflows and see how Videra connects project delivery, lifecycle, governance and evidence in practice.</p>
        </div>
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <a href="https://demo.videra.app/explore" className="inline-flex items-center justify-center bg-white text-slate-900 px-7 py-3.5 rounded-md text-sm font-medium hover:bg-slate-100 transition-colors group" data-testid="v2-home-trial-cta">
            Start my 24-day trial <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>

        <div className="mt-14 pt-10 border-t border-slate-800">
          <p className="text-slate-400 text-sm">Need a platform for another industry?</p>
          <p className="mt-2 text-lg text-white">Use Videra PM to create a project delivery environment around your own sector.</p>
          <a href="https://pm.videra.app" className="mt-5 inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm font-medium">
            Create My Videra <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <p className="mt-12 text-slate-500 italic text-sm">
          See what we&rsquo;ve built. Then imagine what Videra could become for you.
        </p>
      </div>
    </section>
  </div>
  );
};

export default V2Home;
